import { database } from "../../firebaseConfig";
import { ref, push, set, update, remove, get } from "firebase/database";

// Schema version — bump when the clone object shape changes in incompatible ways.
// Older clones will be auto-upgraded on read in useCopyClones.
export const CLONE_SCHEMA_VERSION = 1;

// =============================================================================
// VOICE DNA VERSION — frontend mirror of the backend constant.
//
// The distillation engine stamps every trained clone with the Voice-DNA
// schema version it was produced under (`voiceDNA.version`). The backend
// keeps growing what it extracts (phraseBank, structureProfile, psychTriggers,
// wordDna, voiceContext, …) and bumps VOICE_DNA_VERSION each time. A clone
// trained under an older version is missing those richer blocks — the
// generator then falls back to the abstract DNA only, which is exactly the
// "doesn't sound like my samples" failure mode.
//
// IMPORTANT — keep this in lock-step with VOICE_DNA_VERSION in
// functions/cloneEngine/voiceDnaPhase2Schema.js. We can't import across the
// frontend/functions build boundary, so it's duplicated by design.
// =============================================================================
export const CURRENT_VOICE_DNA_VERSION = 10;

// True when the clone HAS been trained but its Voice DNA was produced under an
// older schema than the engine now supports. Untrained clones return false
// (they're not "outdated", just empty). Drives the "re-train for the new
// intelligence" nudge in the editor + list.
export const isCloneDnaOutdated = (clone) => {
  const dna = clone?.voiceDNA;
  if (!dna) return false;
  const version = Number(dna.version) || 0;
  return version < CURRENT_VOICE_DNA_VERSION;
};

// =============================================================================
// CALIBRATION CATEGORIES — single source of truth on the frontend.
// Mirror of functions/cloneEngine/phraseCategories.js (intentionally
// duplicated, see that file's header for why a require can't cross the
// build boundary). When the backend catalog grows, mirror here too.
// =============================================================================
export const CALIBRATION_CATEGORIES = [
  { id: "greeting",           label: "Greeting",            short: "Greeting",  icon: "fa-hand" },
  { id: "trigger",            label: "Trigger",             short: "Trigger",   icon: "fa-bolt" },
  { id: "relevance",          label: "Relevance",           short: "Relevance", icon: "fa-link" },
  { id: "sender_positioning", label: "Sender Positioning",  short: "Sender",    icon: "fa-id-badge" },
  { id: "audience_fit",       label: "Audience Fit",        short: "Audience",  icon: "fa-users-viewfinder" },
  { id: "problem",            label: "Problem",             short: "Problem",   icon: "fa-triangle-exclamation" },
  { id: "insight",            label: "Insight",             short: "Insight",   icon: "fa-brain" },
  { id: "solution",           label: "Solution",            short: "Solution",  icon: "fa-lightbulb" },
  { id: "proof",              label: "Proof",               short: "Proof",     icon: "fa-shield-halved" },
  { id: "differentiation",    label: "Differentiation",     short: "Differ.",   icon: "fa-arrows-split-up-and-left" },
  { id: "risk_reversal",      label: "Risk Reversal",       short: "Risk",      icon: "fa-shield-heart" },
  { id: "cta",                label: "Call to Action",      short: "CTA",       icon: "fa-bullseye" },
  { id: "signature",          label: "Signature",           short: "Signature", icon: "fa-pen-nib" },
  { id: "other",              label: "Other",               short: "Other",     icon: "fa-circle-question" },
];

export const CALIBRATION_CATEGORY_IDS = CALIBRATION_CATEGORIES.map((c) => c.id);

// =============================================================================
// SUBJECT CALIBRATION CATEGORIES — frontend mirror of the SUBJECT_TACTICS in
// functions/cloneEngine/subjectTactics.js. The subject deck swipes candidate
// subject lines grouped by the open-worthy TACTIC they use (separate from the
// body deck's phrase-intent categories). Keep ids in lock-step with the
// backend taxonomy.
// =============================================================================
export const SUBJECT_CALIBRATION_CATEGORIES = [
  { id: "curiosity_gap",        label: "Curiosity Gap",       short: "Curiosity", icon: "fa-eye" },
  { id: "pattern_interrupt",    label: "Pattern Interrupt",   short: "Interrupt", icon: "fa-bolt" },
  { id: "specific_observation", label: "Specific Observation", short: "Specific", icon: "fa-magnifying-glass" },
  { id: "question_hook",        label: "Question Hook",       short: "Question",  icon: "fa-circle-question" },
  { id: "benefit_tease",        label: "Benefit Tease",       short: "Benefit",   icon: "fa-gift" },
  { id: "personal_direct",      label: "Personal & Direct",   short: "Personal",  icon: "fa-user" },
];

export const SUBJECT_CALIBRATION_CATEGORY_IDS = SUBJECT_CALIBRATION_CATEGORIES.map((c) => c.id);

// Frontend mirror of SUBJECT_CALIBRATION_PROFILE_VERSION in
// functions/cloneEngine/distillSubjectCalibrationPreferences.js. Bump in lock-step.
export const SUBJECT_CALIBRATION_PROFILE_VERSION = 1;

// Empty per-category calibration entry. The "soul" of the model is held in
// `approved` (explicit "yes, this is my voice") and "anti-steered" by
// `rejected` (gentle "this missed it"). Counters drive the confidence ring.
// Approved/rejected lists are capped at 10 each in the writer — older
// feedback gets pushed out as newer training cycles refine the model.
export const createEmptyCalibrationCategory = () => ({
  approved: [],   // [{ text, addedAt }]
  rejected: [],   // [{ text, addedAt }]
  attempts: 0,    // total cards shown (approved + rejected)
  approvedCount: 0,
  rejectedCount: 0,
  lastSessionAt: 0,
});

// Profile schema version — frontend mirror of CALIBRATION_PROFILE_VERSION in
// functions/cloneEngine/distillCalibrationPreferences.js. Bump in lock-step.
export const CALIBRATION_PROFILE_VERSION = 1;

export const createEmptyCalibration = () => ({
  categories: CALIBRATION_CATEGORY_IDS.reduce((acc, id) => {
    acc[id] = createEmptyCalibrationCategory();
    return acc;
  }, {}),
  lastSessionAt: 0,
  totalAttempts: 0,
  totalApproved: 0,
  // Distilled STYLE PREFERENCE PROFILE — the abstract patterns behind the
  // user's ✓/✗ swipes (likes / dislikes / per-category notes / style dials).
  // Produced by the distillCalibrationPreferences cloud function after each
  // session. null until the first distillation runs. This is what actually
  // steers generation (see voiceCloneEmailPrompt.renderPreferenceProfile);
  // the raw approved/rejected lists above are just its training data.
  preferenceProfile: null,
  // HARD REJECTS — the user-curated "worst misses" subset. The full reject
  // pile is intentionally NOT fed to the email writer (too noisy, biases the
  // model away from whole rhythm families that also live in the phrase bank).
  // Instead the user pins only the EXTREME misses; just those reach the
  // generator as a tight, capped "never write like this" overlay. See the
  // HARD REJECTS helper section below.  [{ text, categoryId, addedAt }]
  hardRejects: [],
});

// Subject calibration uses the SAME shape as body calibration, only keyed by
// the subject TACTIC ids instead of body phrase-intent ids. Persisted under
// clone.subjectCalibration so the two decks never collide.
export const createEmptySubjectCalibration = () => ({
  categories: SUBJECT_CALIBRATION_CATEGORY_IDS.reduce((acc, id) => {
    acc[id] = createEmptyCalibrationCategory();
    return acc;
  }, {}),
  lastSessionAt: 0,
  totalAttempts: 0,
  totalApproved: 0,
  preferenceProfile: null,
  hardRejects: [],
});

export const createEmptyClone = () => ({
  schemaVersion: CLONE_SCHEMA_VERSION,
  // `name` here is the PROFILE LABEL (e.g. "Casual Founder Voice") — the
  // recognizable identifier in the user's library and the future master DB.
  // It does NOT represent the sender of an email — that lives on templates.
  name: "",
  avatar: "",
  status: "draft", // draft | training | ready | stale
  voiceConfidence: 0,
  identity: {
    // Style-only fields. Person name / role are intentionally absent — they
    // are sender-specific content and live on the email template that picks
    // this clone. That makes a clone a reusable style asset (think: load
    // "Direct B2B" voice from the master library, attach it to any sender).
    story: "",       // short voice description (sharp, warm, etc.) — calibration only
    tone: "casual",  // casual | formal | mixed — default tonal register
  },
  // NOTE: trust anchors / facts / proof points are intentionally NOT stored
  // on the clone. They are content (what to say) and live on the email
  // template that selects this clone. The clone is style-only.
  language: "auto", // 'auto' | ISO code ('en','de','fr','es','it','nl','pt')
                    // 'auto' lets the distillation engine infer language from samples
  taboos: [],       // string[]  — phrases / patterns the AI must never produce (style-level)
  signOff: {
    // 'auto'  -> sign-off pattern is derived from the sample emails by the
    //            distillation engine and stored in voiceDNA.closingPatterns.
    // 'fixed' -> user pinned a literal string used at the end of every email.
    //            Supports {{firstName}} / {{senderName}} placeholders that the
    //            template fills in at generation time.
    mode: "auto",
    fixed: "",
    // The recurring signature NAME this writer signs with (e.g. "Jan"). The
    // clone is otherwise style-only, but in practice the same person signs the
    // training mails — so we let the writer store their name once and reuse it.
    // Used as a fallback when a template's sign-off identity is "active sender"
    // but the workspace has no resolvable sender name: instead of dropping the
    // name from the closer, generation uses THIS name. Empty = legacy behavior.
    name: "",
  },
  voiceSamples: {
    fullEmails: [],   // [{ id, subject, body, wordCount, addedAt, annotation? }]
                      //  annotation is populated by the distillation engine:
                      //   { dominantTone, inferredAudience, inferredGoal,
                      //     summary, beats: [{ beat, text, techniques[] }] }
    subjectLines: [], // [{ id, text, addedAt, analysis? }]
                      //  analysis is populated by the distillation engine:
                      //   { wordCount, hookType, capitalization, punctuation }
  },
  voiceDNA: null,    // aggregate style profile — populated by distillation
  subjectDNA: null,  // subject-line-specific patterns — populated by distillation
  // CALIBRATION — Tinder-style verification layer the user runs AFTER
  // distillation. The AI generates fresh sample sentences in this clone's
  // voice per category; the user approves / rejects each one. The signal
  // refines the generation prompt (approved = strong style anchor,
  // rejected = soft anti-pattern) WITHOUT replacing the auto-extracted
  // phraseBank from distillation. Re-trainings do NOT wipe calibration:
  // user feedback is durable across re-distillations.
  calibration: createEmptyCalibration(),
  // SUBJECT CALIBRATION — the subject-line counterpart to `calibration`. The
  // AI generates candidate subject lines per tactic; the user swipes ✓/✗; the
  // distilled subjectCalibration.preferenceProfile steers the subject generator
  // (see generateVoiceCloneEmail.renderSubjectPreferenceBlock).
  subjectCalibration: createEmptySubjectCalibration(),
  stats: {
    templatesUsing: 0,
    totalEmailsGenerated: 0,
  },
  createdAt: 0,
  updatedAt: 0,
  lastTrainedAt: 0,
});

// Auto-generates a profile LABEL from the date + a detected topic (from the
// distilled voice context). The label is purely cosmetic — a recognizable
// library identifier; it has NO effect on generation. Kept short + editable.
const AUTO_NAME_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
export const autoCloneName = (clone) => {
  const d = new Date();
  const datePart = `${AUTO_NAME_MONTHS[d.getMonth()]} ${d.getDate()}`;
  const vc = clone?.voiceDNA?.voiceContext || {};
  let topic = "";
  if (Array.isArray(vc.topics) && vc.topics.length > 0) {
    topic = String(vc.topics[0] || "").trim();
  } else if (vc.expertise) {
    topic = String(vc.expertise).split(/[,.;:—-]/)[0].trim();
  } else if (vc.persona) {
    topic = String(vc.persona).split(/[,.;:—-]/)[0].trim();
  }
  if (topic) topic = topic.split(/\s+/).slice(0, 4).join(" ");
  return topic ? `${datePart} (${topic})` : datePart;
};

// The stored signature name this CopyTwin signs with. Trimmed, may be empty.
export const getCloneSignOffName = (clone) =>
  String(clone?.signOff?.name || "").trim();

// Words that are part of a closing PHRASE, never a person's name. Used to
// distinguish "Beste Grüße" (phrase) from the trailing "Jan" (the name) when
// auto-suggesting a signature name from already-detected closing patterns.
const SIGN_OFF_PHRASE_WORDS = new Set(
  [
    // German
    "beste", "besten", "grüße", "gruss", "grüßen", "liebe", "lieben", "viele",
    "vielen", "dank", "danke", "herzliche", "herzlichst", "mit", "freundlichen",
    "freundliche", "freundlichem", "bis", "bald", "dein", "deine", "ihr", "ihre",
    "alles", "gute", "lg", "vg", "mfg", "schöne", "schönen", "tag", "woche",
    // English
    "best", "regards", "kind", "warm", "warmly", "cheers", "thanks", "thank",
    "you", "sincerely", "yours", "talk", "soon", "take", "care", "all", "the",
    "much", "love", "respectfully",
  ].map((w) => w.toLowerCase()),
);

// Cut everything from the first standalone "PS" / "P.S." / "PPS" marker
// onward. A postscript lives AFTER the greeting and would otherwise hide the
// signature name (the name detection reads the last line, which would be the
// PS instead of the greeting). The writer's PS habit is preserved elsewhere —
// here we only need the greeting so the NAME is still recognized.
const stripPostscriptBlock = (text) =>
  String(text || "")
    .replace(/(^|\n|\s)p\.?\s?p?\.?\s?s\b[\s\S]*$/i, "")
    .replace(/\s+$/g, "");

// Heuristic: pull the recurring signature NAME out of the closings the
// distillation engine already detected, so the editor can pre-fill it without
// the user typing it. The writer "oft den selben Namen verwendet" — we read it
// off the greeting (PS stripped) of each closing example and pick the most
// common name-like token group. Returns "" when nothing name-like is found.
export const suggestSignOffName = (clone) => {
  const closings = clone?.voiceDNA?.closingPatterns || [];
  const counts = new Map();

  closings.forEach((c) => {
    const raw = typeof c === "string" ? c : c?.example || c?.pattern || "";
    if (!raw) return;
    // Strip any trailing PS/PPS first so the greeting's name stays visible.
    const greetingOnly = stripPostscriptBlock(raw);
    // The name (if any) lives on the LAST non-empty line of the greeting.
    const lines = String(greetingOnly)
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) return;
    const last = lines[lines.length - 1].replace(/[.,;:!?\s]+$/g, "");

    const tokens = last.split(/\s+/).filter(Boolean);
    // A signature is 1–3 capitalized, alphabetic tokens, none of which is a
    // known closing-phrase word or a literal placeholder.
    const isNameToken = (t) =>
      /^[A-Za-zÀ-ÿ.'-]+$/.test(t) &&
      /^[A-ZÀ-Þ]/.test(t) &&
      !SIGN_OFF_PHRASE_WORDS.has(t.toLowerCase()) &&
      !/^(sender|name|vorname|recipient)$/i.test(t);

    if (tokens.length === 0) return;
    // Collect the TRAILING run of name-like tokens so we also pull "Julian"
    // out of an inline "Viele Grüße, Julian" greeting — not just a name that
    // sits on its own line. Scan from the end, stop at the first non-name
    // token (e.g. the comma-bearing "Grüße," or the phrase word "Grüße").
    const nameTokens = [];
    for (let i = tokens.length - 1; i >= 0 && nameTokens.length < 3; i--) {
      if (isNameToken(tokens[i])) nameTokens.unshift(tokens[i]);
      else break;
    }
    if (nameTokens.length === 0) return;

    const candidate = nameTokens.join(" ");
    counts.set(candidate, (counts.get(candidate) || 0) + 1);
  });

  let best = "";
  let bestCount = 0;
  counts.forEach((count, candidate) => {
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  });
  return best;
};

const clonesPath = (userId) => `/World/${userId}/copyClones`;
const clonePath = (userId, cloneId) => `/World/${userId}/copyClones/${cloneId}`;

export const createClone = async (userId, partial = {}) => {
  const base = createEmptyClone();
  const now = Date.now();
  const newClone = {
    ...base,
    ...partial,
    createdAt: now,
    updatedAt: now,
  };
  const newRef = push(ref(database, clonesPath(userId)));
  await set(newRef, newClone);
  return { id: newRef.key, ...newClone };
};

// Recursively strip `undefined` values — Firebase RTDB rejects them outright
// and the entire write fails. Keeps null (intentional erase) and empty
// strings/arrays/objects intact. Arrays are mapped element-wise.
const stripUndefined = (input) => {
  if (Array.isArray(input)) {
    return input.map(stripUndefined);
  }
  if (input && typeof input === "object") {
    const out = {};
    Object.entries(input).forEach(([key, value]) => {
      if (value === undefined) return;
      out[key] = stripUndefined(value);
    });
    return out;
  }
  return input;
};

export const updateClone = async (userId, cloneId, patch) => {
  if (!userId || !cloneId) return;
  const safePatch = stripUndefined({
    ...patch,
    updatedAt: Date.now(),
  });
  await update(ref(database, clonePath(userId, cloneId)), safePatch);
};

export const deleteClone = async (userId, cloneId) => {
  if (!userId || !cloneId) return;
  await remove(ref(database, clonePath(userId, cloneId)));
};

export const getClone = async (userId, cloneId) => {
  if (!userId || !cloneId) return null;
  const snap = await get(ref(database, clonePath(userId, cloneId)));
  if (!snap.exists()) return null;
  return { id: cloneId, ...snap.val() };
};

// Confidence scoring — deterministic, runs locally before distillation
// improves it. Gives the user immediate feedback as they fill in data.
//
// Style-only weighting. Trust anchors / facts are not part of the clone —
// they live on the per-template campaign config. So this score reflects
// "how well do we know this person's WAY of writing", nothing else.
export const calculateVoiceConfidence = (clone) => {
  if (!clone) return 0;
  let score = 0;

  // Profile (max 25) — label + voice description + tone (all style)
  if (clone.name && clone.name.trim().length >= 3) score += 10;
  if (clone.identity?.story?.length >= 40) score += 15;

  // Style taboos (max 10) — small bonus for explicit phrase blocklist
  const taboos = (clone.taboos || []).filter((t) => t && t.trim());
  score += Math.min(10, taboos.length * 2);

  // Voice samples — heaviest weight. Full emails are the high-signal source,
  // subject lines add a separate dimension. (max 60)
  const fullEmails = clone.voiceSamples?.fullEmails || [];
  const subjectLines = clone.voiceSamples?.subjectLines || [];
  const fullEmailScore = Math.min(40, fullEmails.length * 8); // 5 emails = full credit
  const subjectScore = Math.min(20, subjectLines.length * 2); // 10 subjects = full credit
  score += fullEmailScore + subjectScore;

  // Voice DNA distilled? (max 5 — small bonus, the real benefit is quality)
  if (clone.voiceDNA) score += 5;

  return Math.min(100, Math.round(score));
};

export const confidenceLabel = (score) => {
  if (score >= 85) return { label: "Pro", color: "#2a6776" };       // --darkblue
  if (score >= 60) return { label: "Strong", color: "#6897B7" };    // --blue
  if (score >= 35) return { label: "Learning", color: "#6da7ed" };  // --intenseblue
  return { label: "Draft", color: "#94a3b8" };
};

// =============================================================================
// TRAINING TIERS — recommended sample counts per data type.
// Drives the tier-progress UI in the right-side LivePreview.
//
// Reasoning: more samples ≠ better. Past the "Pro" threshold the LLM's
// attention dilutes and re-training cost rises without quality gain.
// "Overkill" is an intentional warning, not a goal.
// =============================================================================

export const EMAIL_TIERS = [
  { name: "Draft",    min: 0,  color: "#94a3b8" },
  { name: "Starter",  min: 5,  color: "#6da7ed" },
  { name: "Solid",    min: 10, color: "#6897B7" },
  { name: "Pro",      min: 15, color: "#2a6776" },
  { name: "Overkill", min: 25, color: "#f59e0b" },
];

export const SUBJECT_TIERS = [
  { name: "Draft",    min: 0,  color: "#94a3b8" },
  { name: "Starter",  min: 8,  color: "#6da7ed" },
  { name: "Solid",    min: 18, color: "#6897B7" },
  { name: "Pro",      min: 25, color: "#2a6776" },
  { name: "Overkill", min: 40, color: "#f59e0b" },
];

// Bar maxes out at the Pro threshold — Overkill is "beyond optimal".
export const EMAIL_PRO_TARGET = 15;
export const SUBJECT_PRO_TARGET = 25;

export const getTier = (count, tiers) => {
  let current = tiers[0];
  let next = tiers[1] || null;
  for (let i = 0; i < tiers.length; i++) {
    if (count >= tiers[i].min) {
      current = tiers[i];
      next = tiers[i + 1] || null;
    } else {
      break;
    }
  }
  return { current, next, count };
};

export const tierHint = (count, tiers, proTarget) => {
  const { current, next } = getTier(count, tiers);
  if (current.name === "Overkill") {
    return "Diversity matters more than count from here on.";
  }
  if (current.name === "Pro") {
    return "You are at the ideal range.";
  }
  if (current.name === "Solid") {
    const left = proTarget - count;
    return `Sweet spot reached. ${left} more for Pro tier.`;
  }
  if (next) {
    const left = next.min - count;
    return `${left} more for ${next.name} tier.`;
  }
  return "";
};

// =============================================================================
// CALIBRATION HELPERS
//
// Pure functions used by the calibration deck UI + the editor. Persistence
// happens via direct RTDB write at /World/{userId}/copyClones/{cloneId}/calibration
// so the deck stays snappy (no Cloud-Function round-trip per card).
// =============================================================================
const CALIBRATION_LIST_CAP = 10;

const trimEntry = (text) => ({
  text: String(text || "").trim(),
  addedAt: Date.now(),
});

// Confidence per category — flat ratio. Returns null if there's no data
// yet (so the UI can render "not calibrated" instead of "0%").
export const categoryConfidence = (cat) => {
  if (!cat || !cat.attempts) return null;
  return Math.round((cat.approvedCount / cat.attempts) * 100);
};

// Overall calibration confidence — averages over categories that HAVE
// been calibrated. Categories with no attempts are excluded so a fresh
// clone that only got 1 category calibrated doesn't show as "10% overall".
export const overallCalibrationConfidence = (calibration) => {
  if (!calibration?.categories) return null;
  const scores = CALIBRATION_CATEGORY_IDS
    .map((id) => categoryConfidence(calibration.categories[id]))
    .filter((s) => s !== null);
  if (scores.length === 0) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
};

// =============================================================================
// CALIBRATION COVERAGE / MATURITY — orthogonal to "agreement" (approval rate).
//
// Agreement answers "of what I judged, how much sounded like me?".
// Coverage answers "have I judged ENOUGH for the AI to learn the beat?".
// These are different questions and the UI was conflating them: a beat where
// the user rejected everything showed an alarming red 0% even though those
// rejections are GOOD signal. Coverage gives the user the real "am I done /
// what's left?" answer the deck was missing.
// =============================================================================

// Swipes per beat before it counts as "calibrated". Mirrors the backend's
// MIN_EVENTS_TO_DISTILL intent (≥4 swipes = a readable pattern).
export const CALIBRATION_TARGET_PER_CATEGORY = 4;

// Per-beat maturity: untouched → started → calibrated.
export const categoryStatus = (cat) => {
  const attempts = cat?.attempts || 0;
  if (attempts === 0) return "untouched";
  if (attempts < CALIBRATION_TARGET_PER_CATEGORY) return "started";
  return "calibrated";
};

// 0..1 progress toward the per-beat target (how "filled" the ring is).
export const categoryCoverage = (cat) => {
  const attempts = cat?.attempts || 0;
  return Math.min(attempts / CALIBRATION_TARGET_PER_CATEGORY, 1);
};

// How many calibration cards to generate for one category in THIS round.
// Boosts categories that still need coverage or are struggling (many rejects).
export const computeCategoryRoundCount = (cat, basePerCategory = 3) => {
  const attempts = cat?.attempts || 0;
  const approved = Array.isArray(cat?.approved) ? cat.approved.length : 0;
  const rejected = Array.isArray(cat?.rejected) ? cat.rejected.length : 0;

  if (attempts === 0) return 4;

  const gap = CALIBRATION_TARGET_PER_CATEGORY - attempts;
  if (gap > 0) {
    return Math.min(4, Math.max(basePerCategory, gap + 1));
  }

  if (rejected >= 2 && approved < 2) return Math.min(4, basePerCategory + 1);
  if (rejected > approved) return Math.min(4, basePerCategory + 1);

  return Math.max(1, basePerCategory - 1);
};

export const computeCategoryRoundCounts = (calibration, categoryIds, basePerCategory = 3) => {
  const cats = calibration?.categories || {};
  const out = {};
  for (const id of categoryIds) {
    out[id] = computeCategoryRoundCount(cats[id], basePerCategory);
  }
  return out;
};

// Whole-clone coverage roll-up: counts per maturity bucket + the ordered list
// of beats that still need work (untouched first, then under-target). That
// ordered list powers the "calibrate what's left" one-click gap-fill batch.
export const calibrationCoverage = (calibration) => {
  const cats = calibration?.categories || {};
  const total = CALIBRATION_CATEGORY_IDS.length;
  let calibrated = 0;
  let started = 0;
  let untouched = 0;
  const untouchedIds = [];
  const startedIds = [];
  for (const id of CALIBRATION_CATEGORY_IDS) {
    const status = categoryStatus(cats[id]);
    if (status === "calibrated") calibrated += 1;
    else if (status === "started") {
      started += 1;
      startedIds.push(id);
    } else {
      untouched += 1;
      untouchedIds.push(id);
    }
  }
  return {
    total,
    calibrated,
    started,
    untouched,
    // Gap-fill order: never-seen beats first, then half-done ones.
    undercalibrated: [...untouchedIds, ...startedIds],
    percent: Math.round((calibrated / total) * 100),
  };
};

// Readiness stage — the "are you done?" answer in plain words. Drives the
// headline pill + tone so the user always knows where they stand.
export const calibrationReadiness = (calibration) => {
  const { calibrated, started, total, percent } = calibrationCoverage(calibration);
  // "Not calibrated yet" must mean the user has literally swiped nothing.
  // A full default round generates 2 cards/beat, so every beat lands at 2
  // swipes — below the 4-swipe "calibrated" bar. Gating purely on
  // `calibrated === 0` therefore kept the headline stuck on "Not calibrated
  // yet" even after a complete, diligently-swiped round. Once any beat is
  // started we acknowledge the progress ("Getting there") instead.
  if (calibrated === 0 && started === 0) {
    return {
      id: "not_started",
      label: "Not calibrated yet",
      tone: "empty",
      hint: "Swipe a few cards so your CopyTwin learns which sentences sound like you.",
    };
  }
  if (calibrated >= total) {
    return {
      id: "complete",
      label: "Fully calibrated",
      tone: "high",
      hint: "Every beat is dialed in. Run more rounds anytime to keep refining.",
    };
  }
  if (percent >= 60) {
    return {
      id: "solid",
      label: "Well calibrated",
      tone: "high",
      hint: "Solid coverage. Finish the last beats for a fully tuned voice.",
    };
  }
  return {
    id: "getting_there",
    label: "Getting there",
    tone: "mid",
    hint: "Good start. Keep swiping the beats that still need a few cards.",
  };
};

// Update a single category with one new feedback event. Returns the new
// category object (for the caller to write back to RTDB). Approved list
// stays capped — when full, oldest entries drop off. Same for rejected.
export const applyCalibrationFeedback = (cat, { text, approved }) => {
  const base = cat || createEmptyCalibrationCategory();
  const entry = trimEntry(text);
  if (!entry.text) return base;
  const next = {
    approved: [...(base.approved || [])],
    rejected: [...(base.rejected || [])],
    attempts: (base.attempts || 0) + 1,
    approvedCount: base.approvedCount || 0,
    rejectedCount: base.rejectedCount || 0,
    lastSessionAt: Date.now(),
  };
  if (approved) {
    next.approved.unshift(entry);
    if (next.approved.length > CALIBRATION_LIST_CAP) {
      next.approved.length = CALIBRATION_LIST_CAP;
    }
    next.approvedCount += 1;
  } else {
    next.rejected.unshift(entry);
    if (next.rejected.length > CALIBRATION_LIST_CAP) {
      next.rejected.length = CALIBRATION_LIST_CAP;
    }
    next.rejectedCount += 1;
  }
  return next;
};

// Bundle a series of feedback events into one RTDB patch — keeps the deck
// responsive (one write per session, not per card). Caller passes the
// CURRENT calibration block plus the events; we compute the new state
// without mutating the input.
export const aggregateCalibrationEvents = (currentCalibration, events) => {
  const base = currentCalibration || createEmptyCalibration();
  const categories = { ...(base.categories || {}) };
  let addedAttempts = 0;
  let addedApproved = 0;
  for (const ev of events || []) {
    if (!ev?.categoryId || !ev?.text) continue;
    if (!CALIBRATION_CATEGORY_IDS.includes(ev.categoryId)) continue;
    const before = categories[ev.categoryId] || createEmptyCalibrationCategory();
    categories[ev.categoryId] = applyCalibrationFeedback(before, ev);
    addedAttempts += 1;
    if (ev.approved) addedApproved += 1;
  }
  return {
    categories,
    lastSessionAt: events?.length ? Date.now() : (base.lastSessionAt || 0),
    totalAttempts: (base.totalAttempts || 0) + addedAttempts,
    totalApproved: (base.totalApproved || 0) + addedApproved,
    // Carry the distilled profile through untouched. submitFeedback writes
    // the WHOLE calibration object back to RTDB, so if we dropped this here
    // every new session would wipe the learned profile (it's re-distilled
    // out-of-band right after, but until then generation would lose it).
    preferenceProfile: base.preferenceProfile || null,
    // Same reasoning as preferenceProfile: the user's pinned "worst misses"
    // are durable across sessions, so carry them through every swipe flush.
    hardRejects: Array.isArray(base.hardRejects) ? base.hardRejects : [],
  };
};

// Apply A/B-duel verdicts (the second-step calibration refinement). Each
// verdict is { category, championText, challengerText, choice } where choice is
// one of 'challenger' | 'champion' | 'equal':
//   • 'challenger' → the new sentence won: remove the old champion from that
//     category's approved list and promote the challenger in its place.
//   • 'champion' / 'equal' → nothing changes (challenger discarded). Per the
//     product decision, the LOSER is simply dropped — no rejected signal.
// Returns a full calibration object (the whole block is written back to RTDB,
// so preferenceProfile + hardRejects must be carried through untouched).
export const applyCalibrationDuelVerdicts = (currentCalibration, verdicts) => {
  const base = currentCalibration || createEmptyCalibration();
  const categories = { ...(base.categories || {}) };
  const norm = (t) => String(t || "").replace(/\s+/g, " ").trim().toLowerCase();

  for (const v of verdicts || []) {
    if (!v || v.choice !== "challenger") continue;
    if (!v.category || !CALIBRATION_CATEGORY_IDS.includes(v.category)) continue;
    const champText = String(v.championText || "").trim();
    const challText = String(v.challengerText || "").trim();
    if (!challText) continue;

    const cat = categories[v.category] || createEmptyCalibrationCategory();
    // Drop the losing champion (matched case/whitespace-insensitively) and
    // prepend the winner as the new approved anchor for this beat.
    const approved = (cat.approved || []).filter(
      (e) => norm(e?.text) !== norm(champText),
    );
    approved.unshift({ text: challText, addedAt: Date.now() });
    if (approved.length > CALIBRATION_LIST_CAP) approved.length = CALIBRATION_LIST_CAP;

    categories[v.category] = {
      ...cat,
      approved,
      rejected: [...(cat.rejected || [])],
      attempts: cat.attempts || 0,
      // A swap is net-neutral on the historical approval counter.
      approvedCount: cat.approvedCount || 0,
      rejectedCount: cat.rejectedCount || 0,
      lastSessionAt: Date.now(),
    };
  }

  return {
    ...base,
    categories,
    lastSessionAt: Date.now(),
    preferenceProfile: base.preferenceProfile || null,
    hardRejects: Array.isArray(base.hardRejects) ? base.hardRejects : [],
  };
};

// =============================================================================
// HARD REJECTS — the user-curated "worst misses" subset.
//
// Calibration ✗ swipes are deliberately NOT fed to the email generator
// wholesale: there can be dozens, they're noisy, and a long verbatim
// avoid-list biases the model away from whole rhythm/connector families that
// ALSO appear in the writer's real phrase bank (the "calibration distorts the
// urform" failure mode). The fix the user asked for: let them pin only the
// EXTREME misses, and feed JUST those to the generator as a tight, capped
// "never write like this" overlay. The cap keeps the signal sharp and the
// (cached) token cost flat.
// =============================================================================
export const HARD_REJECT_CAP = 5;

const normalizeRejectText = (text) =>
  String(text || "").replace(/\s+/g, " ").trim();

// Flat, most-recent-first, de-duped list of rejected sentences across every
// category — the candidate pool the "worst misses" picker offers the user.
export const collectRejects = (calibration, limit = 24) => {
  const cats = calibration?.categories || {};
  const seen = new Set();
  const out = [];
  for (const id of CALIBRATION_CATEGORY_IDS) {
    const rejected = Array.isArray(cats[id]?.rejected) ? cats[id].rejected : [];
    for (const r of rejected) {
      const text = normalizeRejectText(r?.text);
      if (!text) continue;
      const key = text.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ text, categoryId: id, addedAt: r?.addedAt || 0 });
    }
  }
  return out
    .sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0))
    .slice(0, limit);
};

export const isHardReject = (calibration, text) => {
  const key = normalizeRejectText(text).toLowerCase();
  if (!key) return false;
  return (calibration?.hardRejects || []).some(
    (h) => normalizeRejectText(h?.text).toLowerCase() === key,
  );
};

// Pin / unpin a reject as a "worst miss". Returns a NEW calibration object
// (the caller persists it). Capped at HARD_REJECT_CAP — pinning past the cap
// is a no-op so the generator overlay can never grow into noise.
export const toggleHardReject = (calibration, { text, categoryId }) => {
  const base = calibration || createEmptyCalibration();
  const list = Array.isArray(base.hardRejects) ? base.hardRejects : [];
  const clean = normalizeRejectText(text);
  if (!clean) return base;
  const key = clean.toLowerCase();
  const exists = list.some(
    (h) => normalizeRejectText(h?.text).toLowerCase() === key,
  );
  let hardRejects;
  if (exists) {
    hardRejects = list.filter(
      (h) => normalizeRejectText(h?.text).toLowerCase() !== key,
    );
  } else {
    if (list.length >= HARD_REJECT_CAP) return base; // capped — no-op
    hardRejects = [
      ...list,
      { text: clean, categoryId: categoryId || "", addedAt: Date.now() },
    ];
  }
  return { ...base, hardRejects };
};

// =============================================================================
// SUBJECT CALIBRATION HELPERS — the subject-deck counterparts to the body
// helpers above. Same pure-function shape, only iterating over the subject
// TACTIC ids and operating on clone.subjectCalibration.
// =============================================================================

// Bundle subject swipes into one RTDB patch (mirror of aggregateCalibrationEvents).
export const aggregateSubjectCalibrationEvents = (currentCalibration, events) => {
  const base = currentCalibration || createEmptySubjectCalibration();
  const categories = { ...(base.categories || {}) };
  let addedAttempts = 0;
  let addedApproved = 0;
  for (const ev of events || []) {
    if (!ev?.categoryId || !ev?.text) continue;
    if (!SUBJECT_CALIBRATION_CATEGORY_IDS.includes(ev.categoryId)) continue;
    const before = categories[ev.categoryId] || createEmptyCalibrationCategory();
    categories[ev.categoryId] = applyCalibrationFeedback(before, ev);
    addedAttempts += 1;
    if (ev.approved) addedApproved += 1;
  }
  return {
    categories,
    lastSessionAt: events?.length ? Date.now() : (base.lastSessionAt || 0),
    totalAttempts: (base.totalAttempts || 0) + addedAttempts,
    totalApproved: (base.totalApproved || 0) + addedApproved,
    preferenceProfile: base.preferenceProfile || null,
    hardRejects: Array.isArray(base.hardRejects) ? base.hardRejects : [],
  };
};

// Pin / unpin a rejected subject line (mirror of toggleHardReject).
export const toggleSubjectHardReject = (calibration, { text, categoryId }) => {
  const base = calibration || createEmptySubjectCalibration();
  const list = Array.isArray(base.hardRejects) ? base.hardRejects : [];
  const clean = normalizeRejectText(text);
  if (!clean) return base;
  const key = clean.toLowerCase();
  const exists = list.some(
    (h) => normalizeRejectText(h?.text).toLowerCase() === key,
  );
  let hardRejects;
  if (exists) {
    hardRejects = list.filter(
      (h) => normalizeRejectText(h?.text).toLowerCase() !== key,
    );
  } else {
    if (list.length >= HARD_REJECT_CAP) return base;
    hardRejects = [
      ...list,
      { text: clean, categoryId: categoryId || "", addedAt: Date.now() },
    ];
  }
  return { ...base, hardRejects };
};

// Coverage roll-up over subject tactics (mirror of calibrationCoverage).
export const subjectCalibrationCoverage = (calibration) => {
  const cats = calibration?.categories || {};
  const total = SUBJECT_CALIBRATION_CATEGORY_IDS.length;
  let calibrated = 0;
  let started = 0;
  let untouched = 0;
  const untouchedIds = [];
  const startedIds = [];
  for (const id of SUBJECT_CALIBRATION_CATEGORY_IDS) {
    const status = categoryStatus(cats[id]);
    if (status === "calibrated") calibrated += 1;
    else if (status === "started") {
      started += 1;
      startedIds.push(id);
    } else {
      untouched += 1;
      untouchedIds.push(id);
    }
  }
  return {
    total,
    calibrated,
    started,
    untouched,
    undercalibrated: [...untouchedIds, ...startedIds],
    percent: Math.round((calibrated / total) * 100),
  };
};

// Plain-words readiness for the subject deck (mirror of calibrationReadiness).
export const subjectCalibrationReadiness = (calibration) => {
  const { calibrated, started, total, percent } = subjectCalibrationCoverage(calibration);
  if (calibrated === 0 && started === 0) {
    return {
      id: "not_started",
      label: "Not calibrated yet",
      tone: "empty",
      hint: "Swipe a few subject lines so your CopyTwin learns which subjects sound like you.",
    };
  }
  if (calibrated >= total) {
    return {
      id: "complete",
      label: "Fully calibrated",
      tone: "high",
      hint: "Every subject tactic is dialed in. Run more rounds anytime to keep refining.",
    };
  }
  if (percent >= 60) {
    return {
      id: "solid",
      label: "Well calibrated",
      tone: "high",
      hint: "Solid coverage. Finish the last tactics for a fully tuned subject voice.",
    };
  }
  return {
    id: "getting_there",
    label: "Getting there",
    tone: "mid",
    hint: "Good start. Keep swiping the tactics that still need a few cards.",
  };
};

// Overall agreement % across calibrated subject tactics.
export const overallSubjectCalibrationConfidence = (calibration) => {
  if (!calibration?.categories) return null;
  const scores = SUBJECT_CALIBRATION_CATEGORY_IDS
    .map((id) => categoryConfidence(calibration.categories[id]))
    .filter((s) => s !== null);
  if (scores.length === 0) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
};

// Flat, de-duped reject pool across subject tactics (mirror of collectRejects).
export const collectSubjectRejects = (calibration, limit = 24) => {
  const cats = calibration?.categories || {};
  const seen = new Set();
  const out = [];
  for (const id of SUBJECT_CALIBRATION_CATEGORY_IDS) {
    const rejected = Array.isArray(cats[id]?.rejected) ? cats[id].rejected : [];
    for (const r of rejected) {
      const text = normalizeRejectText(r?.text);
      if (!text) continue;
      const key = text.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ text, categoryId: id, addedAt: r?.addedAt || 0 });
    }
  }
  return out
    .sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0))
    .slice(0, limit);
};

export const generateSampleId = () =>
  `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

export const countWords = (text) =>
  (text || "").trim().split(/\s+/).filter(Boolean).length;

// =============================================================================
// SAMPLE NORMALIZATION
//
// Pasted emails (Gmail, Outlook, Word) bring a lot of garbage that pollutes
// Voice DNA distillation: NBSPs, zero-width chars, smart-quote artefacts,
// random extra blank lines, trailing spaces. We strip all of that on save
// so the engine sees a clean version of the writer's actual style — never
// the copy-paste noise.
//
// What we DO keep: the writer's intentional paragraph rhythm. One blank
// line between paragraphs is preserved; runs of 3+ newlines are clamped
// down to 2 (one blank line = paragraph break).
// =============================================================================
export const normalizeSampleText = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  return raw
    // Line endings — Windows / old Mac → Unix
    .replace(/\r\n?/g, "\n")
    // Whitespace garbage that copy-paste loves to inject
    .replace(/\u00A0/g, " ")        // non-breaking space
    .replace(/[\u200B-\u200D\uFEFF]/g, "") // zero-width chars + BOM
    .replace(/\t/g, " ")            // tabs → spaces (we never want tabs in prose)
    // Collapse runs of spaces inside a line (but never across lines)
    .replace(/[ \f\v]{2,}/g, " ")
    // Strip trailing whitespace on every line
    .split("\n")
    .map((line) => line.replace(/[ \t]+$/g, ""))
    .join("\n")
    // Clamp 3+ consecutive newlines to 2 (= one blank line = paragraph break)
    .replace(/\n{3,}/g, "\n\n")
    // Outer trim — leading / trailing blank lines
    .trim();
};
